/**
 * SampleLoginView — the Sample Login workspace (stepper steps 2–4), faithful to
 * the approved prototype and the legacy ALPACA / BTLIMS screens.
 *
 * A real TAB CONTROL with its own body per tab; the record-level Save actions
 * belong to the whole registration and are supplied by the host page:
 *
 *   Sample Login  — the wide sample grid (30+ configurable columns, the first
 *                   two FROZEN) + the Test Assignment panel for the current row.
 *   Test View     — one row per sample × test (editable IsGroup + Status).
 *   Containers    — bottle allocation, By Sample (master-detail) or All in One.
 *   Parameters    — one row per sample × test × analyte; FIELD-method results
 *                   are keyed here, lab methods stay read-only (Result Entry).
 *
 * Legacy behaviours reproduced:
 *   · one current record (the ▶ row) drives Test Assignment — BTLIMS parity;
 *   · right-click an editable cell → Copy to All Cells / Copy to Selected Rows
 *     (the shared <CellContextMenu>);
 *   · Copy Samples clones the marked sample (fields + tests + bottles) N times;
 *   · Copy Test copies one sample's test set onto other samples;
 *   · tests sharing a container + preservative share ONE bottle, and Qty is the
 *     number of DISTINCT bottles (never Qty 1 with 5 bottles);
 *   · Find Column jumps to and flashes any of the 30+ columns.
 *
 * TODO(AI-29131): every mutation is local state.  The sample / test / bottle
 * endpoints must accept these shapes (see srData.ts) and re-derive the bottle
 * grouping + hold times server-side (§1 — business logic lives in the API).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { CellContextMenu, canBulkCopy } from '../../../components/gridEdit';
import { ExportMenu } from '../../../components/AppToolbar';
import { api } from '../../../api/client';
import { fmtLocalDateTime } from '../../../utils/datetime';
import {
  SAMPLE_STATUS_OPTS, SL_COLS,
  autoBottles, cloneTest, copyTest, mkTest, normBottle, renumber,
  storageCondition, syncQty, testDefaults, toDateInput, isFieldTest,
  type BottleLookups, type SrParamData, type SrSample, type SrTest,
} from './srData';

// ── Test Parameter cache (DB-driven, replaces hardcoded PARAMS) ─────────

/** Cache key = "testName|method|comp" → parameter name list. */
const _paramCache = new Map<string, string[]>();
/** Parallel cache: same key → { parameterName → testparameterOid }. */
const _paramOidCache = new Map<string, Record<string, string>>();
let _paramVer = 0;

function paramCacheKey(t: { name: string; method: string; comp: string }): string {
  return `${t.name}|${t.method}|${t.comp || 'Default'}`;
}

export async function fetchTestParams(t: { name: string; method: string; comp: string }): Promise<string[]> {
  const k = paramCacheKey(t);
  const cached = _paramCache.get(k);
  if (cached) return cached;
  try {
    const res = await api.get('/api/v1/sample-registration/test-parameters', {
      params: { testName: t.name, methodNumber: t.method, component: t.comp || 'Default' },
    });
    const rows: { parameterName: string; testparameterOid: string }[] = res.data?.data ?? [];
    const names: string[] = rows.map(r => r.parameterName);
    const oidMap: Record<string, string> = {};
    for (const r of rows) oidMap[r.parameterName] = r.testparameterOid;
    if (names.length === 0) names.push(t.name);
    _paramCache.set(k, names);
    _paramOidCache.set(k, oidMap);
    _paramVer++;
    return names;
  } catch {
    const fallback = [t.name];
    _paramCache.set(k, fallback);
    _paramVer++;
    return fallback;
  }
}

function cachedParams(t: { name: string; method: string; comp: string }): string[] | undefined {
  return _paramCache.get(paramCacheKey(t));
}

/** Resolve parameterName → Testparameter.Oid for a given test. */
export function cachedParamOid(t: { name: string; method: string; comp: string }, paramName: string): string | undefined {
  return _paramOidCache.get(paramCacheKey(t))?.[paramName];
}

function paramLabel(t: SrTest): string {
  const list = cachedParams(t);
  if (!list) return '…';
  const total = list.length;
  const n = t.params ? Object.values(t.params).filter(Boolean).length : total;
  return n === total ? 'All Param' : 'Customized';
}

// ── Bottle / Container reference lookups (module-level cache) ────────────────

let _bottleLookups: BottleLookups | null = null;
let _bottleLookupsPending: Promise<BottleLookups> | null = null;

async function fetchBottleLookups(): Promise<BottleLookups> {
  if (_bottleLookups) return _bottleLookups;
  if (_bottleLookupsPending) return _bottleLookupsPending;
  _bottleLookupsPending = api.get('/api/v1/sample-registration/bottle-lookups')
    .then(res => {
      const d = res.data?.data;
      _bottleLookups = {
        containers: d?.containers ?? [],
        preservatives: d?.preservatives ?? [],
        storages: d?.storages ?? [],
        storageConditions: d?.storageConditions ?? [],
      };
      return _bottleLookups;
    })
    .catch(() => {
      _bottleLookups = { containers: [], preservatives: [], storages: [], storageConditions: [] };
      return _bottleLookups;
    })
    .finally(() => { _bottleLookupsPending = null; });
  return _bottleLookupsPending;
}

function useBottleLookups(): BottleLookups {
  const [bl, setBl] = useState<BottleLookups>(_bottleLookups ?? {
    containers: [], preservatives: [], storages: [], storageConditions: [],
  });
  useEffect(() => {
    if (_bottleLookups) { setBl(_bottleLookups); return; }
    fetchBottleLookups().then(setBl);
  }, []);
  return bl;
}

// ── Sample Login config (from AI_SampleRegistration_GetSampleLoginConfig) ────

interface LoginFieldCfg {
  fieldId: string;
  caption: string;
  width: number;
  sortOrder: number;
  freeze: boolean;
  fieldType: string;
  lookupSource: string | null;
}

interface LoginLookupOpt {
  oid: string;
  name: string;
  visualMatrixGuids: string;
  address: string;
  samplingAddress: string;
  keyMap: string;
  samplePointId: string;
  samplePointType: string;
  pwsId: string;
  waterTypeName: string;
  systemTypeOid: string;
  rejectionCriteriaOid: string;
  parentSampleID: string;
  parentSampleDate: string;
}

interface SampleLoginCfg {
  fields: LoginFieldCfg[];
  visualMatrices: LoginLookupOpt[];
  sampleTypes: LoginLookupOpt[];
  collectors: LoginLookupOpt[];
  sampleStatuses: LoginLookupOpt[];
  sampleSites: LoginLookupOpt[];
  systemTypes: LoginLookupOpt[];
  qcCategories: LoginLookupOpt[];
  departments: LoginLookupOpt[];
  rejectionCriteria: LoginLookupOpt[];
  repeatLocations: LoginLookupOpt[];
}

// Maps XAF FieldID names (from dbo.SampleMatrixSetupFields) to the SrSample.v[]
// keys used throughout this module. When a FieldID matches an SL_COLS key exactly
// it is omitted (the identity mapping is the default: vKey = fieldId ?? fieldId).
const FIELD_ID_TO_V_KEY: Record<string, string> = {
  ClientSampleID:     'SampleName',
  StationLocation:    'SiteName',
  CollectDate:        'DateCollected',
  CollectTimeDisplay: 'TimeCollected',
  VisualMatrix:       'SampleMatrix',   // OID stored under SL_COLS key
  ChlorineFree:       'FreeColorine(mg/L)',
  ChlorineTotal:      'TotalChlorine(mg/L)',
  LoginBy:            'EnteredBy',
  LoginDateTime:      'EnteredDate',
};

// Lookup list key → config property name. The SP returns lookupSource as the
// camelCase key matching SampleLoginCfg properties — this helper resolves the
// options array at runtime so the column builder is fully SP-driven.
const getLookupOpts = (cfg: SampleLoginCfg, src: string | null): LoginLookupOpt[] => {
  if (!src) return [];
  return (cfg as unknown as Record<string, LoginLookupOpt[] | undefined>)[src] ?? [];
};

export type SlTab = 'login' | 'testview' | 'containers' | 'params';

export interface SampleLoginViewProps {
  jobId: string;
  /** The server-assigned GUID of the job, used to fetch the matrix-driven
   *  Sample Login column config.  Null for a new, never-saved job. */
  jobOid?: string | null;
  /** OID of the primary (first) selected matrix — used to load the column config
   *  for new jobs that do not have a jobOid yet.  Null when no matrix selected. */
  primaryMatrixOid?: string | null;
  /** OID of the client chosen on the registration header — used to filter the
   *  SampleSites dropdown to that client's sites in New Mode. */
  clientOid?: string | null;
  samples: SrSample[];
  /** Replace the sample set (the host owns the state; this view mutates a copy). */
  setSamples: (next: SrSample[]) => void;
  tab: SlTab;
  setTab: (t: SlTab) => void;
  /** The current record index (the ▶ row). */
  active: number;
  setActive: (i: number) => void;
  /** Record-level actions rendered on the tab strip (Save / Save and Close / Cancel). */
  actions: React.ReactNode;
  /** Return to the full Registration detail view (step 1). */
  onBackToRegistration: () => void;
  onWarn: (message: string) => void;
  onInfo: (message: string) => void;
  onSuccess: (message: string) => void;
  confirmAction: (title: string, message: string, run: () => void, danger?: boolean) => void;
  /** Open the "New Collector" quick-add from the Sample Login toolbar. */
  onNewCollector: () => void;
  /** Live test catalog rows from the API: [name, method, isGroup, component]. */
  testRows?: string[][];
  /** Group test name → child tests (expanded from GroupTestMethod). */
  groupChildren?: Record<string, { name: string; method: string; comp: string }[]>;
  /** OID → display name for VisualMatrix (built from the matrix lookup). */
  matrixMap?: Record<string, string>;
}

export function SampleLoginView(props: SampleLoginViewProps) {
  const { samples, tab, setTab, actions, onBackToRegistration } = props;

  return (
    <section className="sr-sec">
      <div className="sr-sec-b" style={{ paddingBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="ca-btn" onClick={onBackToRegistration}
            title="Back to the Sample Registration detail view">
            <span style={{ color: 'var(--ai-blue)' }}>‹</span> Sample Registration
          </button>
          <div className="sr-tabbar" style={{ flex: 1, margin: 0 }} role="tablist">
            {([
              ['login', 'Sample Login'], ['testview', 'Test View'],
              ['containers', 'Containers'], ['params', 'Parameters'],
            ] as [SlTab, string][]).map(([id, label]) => (
              <button key={id} role="tab" aria-selected={tab === id}
                className={`sr-tb${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>
                {label}
              </button>
            ))}
          </div>
          {actions}
        </div>

        <div style={{ paddingTop: 10 }}>
          {tab === 'login' && <LoginTab {...props} />}
          {tab === 'testview' && <TestViewTab {...props} />}
          {tab === 'containers' && <ContainersTab {...props} />}
          {tab === 'params' && <ParametersTab {...props} />}
        </div>

        {samples.length === 0 && (
          <p className="sr-note" style={{ marginTop: 8 }}>
            No samples yet — set <b>No Of Samples</b> on the Registration step, then return here.
          </p>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** The static boolean Hold column appended at the far right when the matrix
 *  config does not include its own Hold field.  Defined outside the component
 *  to avoid recreating the column def on every render. */
function staticHoldCol(
  mutate: (fn: (draft: SrSample[]) => void) => void,
  samples: SrSample[],
): AppColumn<SrSample> {
  return {
    field: 'hold', header: 'Hold', width: 54, align: 'center',
    sortable: false, filterable: false,
    render: s => (
      <input type="checkbox" checked={s.hold} aria-label={`Hold ${s.id}`}
        onClick={e => e.stopPropagation()}
        onChange={e => {
          const checked = e.target.checked;
          mutate(draft => { const t = draft.find(x => x.id === s.id); if (t) t.hold = checked; });
        }} />
    ),
  };
  // samples is a dep only so the column def rebuilds when sample set changes (closure)
  void samples;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample Login tab — the wide grid + Test Assignment
// ─────────────────────────────────────────────────────────────────────────────
function LoginTab({
  jobId, jobOid, primaryMatrixOid, clientOid, samples, setSamples, active, setActive, onWarn,
  onSuccess, confirmAction, onNewCollector, setTab, testRows = [], groupChildren = {},
}: SampleLoginViewProps) {
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyCount, setCopyCount] = useState(1);
  const [copyTestOpen, setCopyTestOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [ctx, setCtx] = useState<{ col: string; label: string; value: string; x: number; y: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Dynamic Sample Login config (matrix-driven columns + lookup lists) ────
  const [config, setConfig] = useState<SampleLoginCfg | null>(null);

  useEffect(() => {
    let dead = false;
    let url: string | null = null;
    if (jobOid) {
      // Edit mode: fetch config keyed by the saved job so the SP can resolve
      // the primary matrix from Samplecheckin.SampleMatries.
      url = `/api/v1/sample-registration/${jobOid}/sample-login-config`;
    } else if (primaryMatrixOid) {
      // New mode: job not yet saved — load config directly by matrix OID.
      const params = clientOid ? `?clientOid=${clientOid}` : '';
      url = `/api/v1/sample-registration/sample-login-config/by-matrix/${primaryMatrixOid}${params}`;
    }
    if (!url) return;
    api.get(url)
      .then(r => {
        if (!dead) setConfig((r.data as { data: SampleLoginCfg }).data ?? null);
      })
      .catch(() => { /* non-critical — falls back to static SL_COLS */ });
    return () => { dead = true; };
  }, [jobOid, primaryMatrixOid, clientOid]);

  /** Mutate a copy of the sample set and publish it. */
  const mutate = useCallback((fn: (draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    fn(draft);
    setSamples(draft);
  }, [samples, setSamples]);

  // Qty is the user's BOTTLE COUNT — it drives the bottle list in the picker
  // ({SampleID}-A…), so it must not be recomputed from the current bottles here
  // (that is `syncQty`, and it belongs only after a MANUAL bottle change).
  const setCell = (rowId: string, col: string, value: string) => {
    mutate(draft => {
      const s = draft.find(x => x.id === rowId);
      if (!s) return;
      s.v[col] = value;
      // Cascade sample status to all tests within the sample and sync SubOut
      if (col === 'SampleStatus' && config) {
        const opts = getLookupOpts(config, 'sampleStatuses');
        const statusName = opts.find(o => o.oid === value)?.name ?? '';
        const isSub = statusName === 'Sub Out';
        s.v['SubOut'] = isSub ? '1' : '0';
        for (const t of s.tests) { t.status = statusName; t.subOut = isSub; }
      }
    });
  };

  const copyColumn = (col: string, displayValue: string, onlySelected: boolean) => {
    // When config is loaded and the column is a FK dropdown, resolve name → OID
    // so the stored value matches what the dynamic render/commit expects.
    let storeValue = displayValue;
    if (config) {
      const fCfg = config.fields.find(f => (FIELD_ID_TO_V_KEY[f.fieldId] ?? f.fieldId) === col);
      if (fCfg?.fieldType === 'dropdown' && fCfg.lookupSource) {
        const opts = getLookupOpts(config, fCfg.lookupSource);
        const found = opts.find(o => o.name === displayValue);
        storeValue = found?.oid ?? displayValue;
      }
    }
    mutate(draft => {
      for (const s of draft) {
        if (onlySelected && !sel.has(s.id)) continue;
        s.v[col] = storeValue;
      }
    });
    onSuccess(`Copied "${displayValue}" to ${onlySelected ? sel.size : samples.length} cell(s).`);
  };

  // ── Helper: OID → display name for FK columns ─────────────────────────────
  const fkName = (opts: LoginLookupOpt[], oid: string) =>
    opts.find(o => o.oid === oid)?.name ?? oid;

  // ── Columns: ▶ record indicator · SampleID (frozen) · the configurable set · Hold
  const cols: AppColumn<SrSample>[] = useMemo(() => {
    const out: AppColumn<SrSample>[] = [
      {
        field: '__rec', header: '', width: 26, align: 'center',
        sortable: false, filterable: false, noExport: true,
        render: s => (samples[active]?.id === s.id
          ? <span style={{ color: 'var(--brand-accent, var(--ai-orange))', fontSize: 11 }}>▶</span>
          : null),
      },
      {
        field: 'id', header: 'SampleID', width: 150,
        render: s => (
          <span style={{ fontWeight: 600 }}>
            {s.id} <span className="sr-hint-inline" style={{ display: 'inline' }}>({s.tests.length})</span>
          </span>
        ),
        text: s => s.id,
      },
    ];

    if (config) {
      // ── Dynamic columns driven by dbo.SampleMatrixSetupFields ────────────
      // Field type and lookup source come from the SP (RS1 FieldType /
      // LookupSource) — no hardcoded FK_LOOKUP / BOOL_FIELDS / DATE_FIELDS /
      // RO_FIELDS constants.
      for (const f of config.fields) {
        const { fieldId, caption, width, fieldType, lookupSource } = f;

        // SampleID is already rendered as the frozen first column.
        if (fieldId === 'SampleID') continue;

        // Translate XAF FieldID → the SrSample.v[] key used throughout this
        // module (SL_COLS-compatible names so static and dynamic paths share
        // the same v[] slots and the save body works for both cases).
        const vKey = FIELD_ID_TO_V_KEY[fieldId] ?? fieldId;

        if (fieldType === 'bool') {
          const statusDriven = vKey === 'SubOut';
          out.push({
            field: vKey, header: caption, width,
            align: 'center', sortable: false, filterable: false,
            render: s => (
              <input type="checkbox"
                checked={s.v[vKey] === '1'}
                disabled={statusDriven}
                aria-label={`${caption} ${s.id}`}
                title={statusDriven ? "Driven by SampleStatus — set status to 'Sub Out' to activate" : undefined}
                onClick={e => e.stopPropagation()}
                onChange={statusDriven ? undefined : e => {
                  const chk = e.target.checked;
                  mutate(draft => {
                    const t = draft.find(x => x.id === s.id);
                    if (t) t.v[vKey] = chk ? '1' : '0';
                  });
                }} />
            ),
          });
        } else if (fieldType === 'dropdown') {
          const opts = getLookupOpts(config, lookupSource);
          const col: AppColumn<SrSample> = {
            field: vKey, header: caption, width,
            value: s => fkName(opts, s.v[vKey] ?? ''),
            text:  s => fkName(opts, s.v[vKey] ?? ''),
            edit: {
              type: 'combo',
              // SampleType cascade: filter by the row's VisualMatrix
              options: lookupSource === 'sampleTypes'
                ? (row: SrSample) => {
                    const vmOid = row.v['SampleMatrix'] ?? '';
                    return config.sampleTypes
                      .filter(st =>
                        !vmOid ||
                        !st.visualMatrixGuids ||
                        st.visualMatrixGuids.toUpperCase().includes(vmOid.toUpperCase()))
                      .map(st => st.name);
                  }
                // RejectionCriteria cascade: filter by the row's VisualMatrix
                : lookupSource === 'rejectionCriteria'
                ? (row: SrSample) => {
                    const vmOid = row.v['SampleMatrix'] ?? '';
                    return config.rejectionCriteria
                      .filter(rc =>
                        !vmOid ||
                        !rc.visualMatrixGuids ||
                        rc.visualMatrixGuids.toUpperCase().includes(vmOid.toUpperCase()))
                      .map(rc => rc.name);
                  }
                : opts.map(o => o.name),
            },
            onCellContextMenu: (e, s) => {
              const value = fkName(opts, s.v[vKey] ?? '');
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            },
          };
          out.push(col);
        } else if (fieldType === 'time') {
          // Time-only field (HH:mm input)
          const col: AppColumn<SrSample> = {
            field: vKey, header: caption, width,
            value: s => s.v[vKey] ?? '',
            text:  s => s.v[vKey] ?? '',
            edit: { type: 'text' },
            onCellContextMenu: (e, s) => {
              const value = s.v[vKey] ?? '';
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            },
          };
          out.push(col);
        } else if (fieldType === 'number') {
          // Numeric field — text input, display raw value
          const col: AppColumn<SrSample> = {
            field: vKey, header: caption, width,
            value: s => s.v[vKey] ?? '',
            text:  s => s.v[vKey] ?? '',
            edit: { type: 'text' },
            onCellContextMenu: (e, s) => {
              const value = s.v[vKey] ?? '';
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            },
          };
          out.push(col);
        } else {
          // text / date / datetime / ro
          const isDate     = fieldType === 'date';
          const isDatetime = fieldType === 'datetime';
          const isRO       = fieldType === 'ro';

          const renderText = (raw: string) => {
            if (isDate)     return toDateInput(raw);
            if (isDatetime) return raw ? fmtLocalDateTime(raw) : '';
            return raw;
          };
          const col: AppColumn<SrSample> = {
            field: vKey, header: caption, width,
            value: s => renderText(s.v[vKey] ?? ''),
            text:  s => renderText(s.v[vKey] ?? ''),
          };
          if (!isRO && !isDatetime) {
            col.edit = { type: isDate ? 'date' : 'text' };
            col.onCellContextMenu = (e, s) => {
              const value = s.v[vKey] ?? '';
              if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
              setCtx({ col: vKey, label: caption, value, x: e.clientX, y: e.clientY });
            };
          }
          out.push(col);
        }
      }

      // Add static boolean Hold only when matrix config does NOT include it
      // (the Hold FieldID in config takes precedence)
      const hasHoldField = config.fields.some(f => f.fieldId === 'Hold');
      if (!hasHoldField) {
        out.push(staticHoldCol(mutate, samples));
      }
    } else {
      // ── Static fallback when config hasn't loaded yet (SL_COLS) ──────────
      for (const [key, type, width] of SL_COLS) {
        const readOnly = type === 'ro';
        const col: AppColumn<SrSample> = {
          field: key, header: key, width,
          value: s => s.v[key] ?? '',
          text: s => (type === 'date' ? toDateInput(s.v[key] ?? '') : (s.v[key] ?? '')),
        };
        if (!readOnly) {
          col.edit = {
            type: type === 'status' ? 'combo'
              : type === 'qty' ? 'combo'
                : type === 'date' ? 'date' : 'text',
            options: type === 'status' ? SAMPLE_STATUS_OPTS
              : type === 'qty' ? ['1', '2', '3', '4', '5', '6'] : undefined,
          };
          col.onCellContextMenu = (e, s) => {
            const value = s.v[key] ?? '';
            if (!canBulkCopy(value)) { onWarn('Cell is empty — nothing to copy.'); return; }
            setCtx({ col: key, label: key, value, x: e.clientX, y: e.clientY });
          };
        }
        out.push(col);
      }
      out.push(staticHoldCol(mutate, samples));
    }

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, samples, active, mutate, onWarn]);

  // ── Find Column locator ───────────────────────────────────────────────────
  const columnNames = useMemo(
    () => config
      ? ['SampleID', ...config.fields.map(f => f.caption)]
      : ['SampleID', ...SL_COLS.map(c => c[0]), 'Hold'],
    [config],
  );
  const [find, setFind] = useState('');
  const [findOpen, setFindOpen] = useState(false);
  const findMatches = useMemo(() => {
    const q = find.trim().toLowerCase();
    return q ? columnNames.filter(c => c.toLowerCase().includes(q)) : columnNames;
  }, [find, columnNames]);

  const jumpToColumn = (name: string) => {
    setFindOpen(false);
    setFind(name);
    const wrap = scrollRef.current?.querySelector('.adg-scroll') as HTMLElement | null;
    if (!wrap) return;
    const heads = Array.from(wrap.querySelectorAll('th')) as HTMLElement[];
    const th = heads.find(h => (h.querySelector('.adg-th-label')?.textContent ?? '').trim() === name);
    if (!th) return;
    const target = wrap.scrollLeft + (th.getBoundingClientRect().left - wrap.getBoundingClientRect().left) - 320;
    wrap.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  };

  // ── Toolbar actions ───────────────────────────────────────────────────────
  const addSample = () => {
    mutate(draft => {
      const template = draft[active] ?? draft[0];
      const s: SrSample = {
        id: '', hold: false,
        v: { Qty: '1', SampleMatrix: template?.v.SampleMatrix ?? '' },
        tests: (template?.tests ?? []).map(cloneTest),
      };
      autoBottles(s);
      draft.push(s);
      renumber(draft, jobId);
    });
    onSuccess('Sample added.');
  };

  const deleteCurrent = () => {
    if (samples.length < 2) { onWarn('At least one sample is required.'); return; }
    const gone = samples[active]?.id ?? '';
    confirmAction('Delete sample', `Delete ${gone}? This cannot be undone.`, () => {
      mutate(draft => { draft.splice(active, 1); renumber(draft, jobId); });
      setActive(Math.max(0, Math.min(active, samples.length - 2)));
      onSuccess(`Deleted ${gone}.`);
    }, true);
  };

  const runCopySamples = () => {
    const src = samples[active];
    if (!src) { onWarn('No sample selected.'); return; }
    const n = Math.max(1, copyCount);
    mutate(draft => {
      const source = draft[active];
      if (!source) return;
      const clones = Array.from({ length: n }, () => ({
        id: '', hold: source.hold,
        v: { ...source.v },
        tests: source.tests.map(cloneTest),
      }));
      draft.splice(active + 1, 0, ...clones);
      renumber(draft, jobId);
    });
    setCopyOpen(false);
    onSuccess(`${n} cop${n > 1 ? 'ies' : 'y'} of ${src.id} added.`);
  };

  // The first two columns (▶ indicator + SampleID) are always frozen.
  // Additional frozen columns come from dbo.SampleMatrixSetupFields.Freeze — count
  // the leading consecutive freeze=true fields in config (skipping none since the
  // indicator + SampleID are prepended by cols, not from config.fields).
  const stickyCount = useMemo(() => {
    if (!config) return 2;
    let extra = 0;
    for (const f of config.fields) {
      if (f.freeze) extra++;
      else break;
    }
    return 2 + extra;
  }, [config]);

  const exportMatrix = useMemo(() => {
    if (config) {
      return {
        headers: ['SampleID', ...config.fields.map(f => f.caption)],
        rows: samples.map(s => [
          s.id,
          ...config.fields.map(f => {
            const vKey = FIELD_ID_TO_V_KEY[f.fieldId] ?? f.fieldId;
            if (f.fieldType === 'dropdown' && f.lookupSource) {
              const opts = getLookupOpts(config, f.lookupSource);
              return fkName(opts, s.v[vKey] ?? '');
            }
            if (f.fieldType === 'bool') return s.v[vKey] === '1' ? 'Yes' : 'No';
            return s.v[vKey] ?? '';
          }),
        ]),
      };
    }
    return {
      headers: ['SampleID', ...SL_COLS.map(c => c[0]), 'Hold'],
      rows: samples.map(s => [s.id, ...SL_COLS.map(c => s.v[c[0]] ?? ''), s.hold ? 'Yes' : 'No']),
    };
  }, [config, samples]);

  return (
    <div className="sr-sl-layout">
      <div className="sr-sl-panel sr-sl-samples">
      <div className="sr-subtoolbar" style={{ marginBottom: 8 }}>
        <button className="ca-btn" onClick={deleteCurrent}><span className="tb-i tb-i-red">🗑</span> Delete</button>
        <button className="ca-btn" onClick={addSample}><span className="tb-i tb-i-green">＋</span> Add Sample</button>
        <button className="ca-btn" onClick={onNewCollector}>
          <span className="tb-i tb-i-green">＋</span> Add Collector
        </button>
        <button className="ca-btn" onClick={() => setSiteOpen(true)}>
          <span className="tb-i tb-i-green">＋</span> Add Site
        </button>
        <span className="app-toolbar__divider" aria-hidden="true" />
        <button className="ca-btn" onClick={() => { setCopyCount(1); setCopyOpen(true); }}>
          <span className="tb-i tb-i-blue">🗎</span> Copy Samples
        </button>
        <button className="ca-btn" onClick={() => {
          if (samples.length < 2) { onWarn('Add at least two samples before copying tests between them.'); return; }
          setCopyTestOpen(true);
        }}>
          <span className="tb-i tb-i-blue">🗎</span> Copy Test
        </button>
        <button className="ca-btn" onClick={() => setTab('testview')}>
          <span className="tb-i tb-i-blue">✎</span> Test Edit
        </button>
        <button className="ca-btn" disabled title="Enabled once results exist">Reanalysis</button>
        <span className="app-toolbar__divider" aria-hidden="true" />
        <ExportMenu filename={`sample-login-${jobId || 'new'}`} {...exportMatrix} triggerClassName="ca-btn" />
      </div>

      {/* Find Column — the grid is 30+ columns wide */}
      <div className="sr-colfind">
        <span aria-hidden="true">🔍</span>
        <input
          placeholder="Find column… (e.g. Collector, PWSID, Free Chlorine)"
          value={find}
          autoComplete="off"
          onChange={e => { setFind(e.target.value); setFindOpen(true); }}
          onFocus={() => setFindOpen(true)}
          onBlur={() => setTimeout(() => setFindOpen(false), 120)}
          onKeyDown={e => { if (e.key === 'Enter' && findMatches[0]) jumpToColumn(findMatches[0]); }}
        />
        <span className="sr-colfind-hint">of {columnNames.length} columns — jumps to the field</span>
        {findOpen && (
          <div className="sr-colfind-menu">
            {findMatches.length === 0
              ? <div className="sr-colfind-item" style={{ color: '#9aa1ad' }}>No matching column.</div>
              : findMatches.map(c => (
                <button key={c} className="sr-colfind-item"
                  onMouseDown={e => { e.preventDefault(); jumpToColumn(c); }}>{c}</button>
              ))}
          </div>
        )}
      </div>

      <div ref={scrollRef} className="sr-sl-grid-wrap">
        <AppDataGrid<SrSample>
          rows={samples}
          columns={cols}
          getRowId={s => s.id}
          selectable
          selectedIds={sel}
          onSelectionChange={setSel}
          stickyColumns={stickyCount}
          onRowClick={s => setActive(samples.findIndex(x => x.id === s.id))}
          onCellCommit={(row, col, value) => {
            // When the config is loaded, FK combo edits emit the display name;
            // resolve it back to the OID before storing so the render and save
            // path both work with consistent OID values.
            if (config) {
              const fCfg = config.fields.find(fc => (FIELD_ID_TO_V_KEY[fc.fieldId] ?? fc.fieldId) === col.field);
              if (fCfg?.fieldType === 'dropdown' && fCfg.lookupSource) {
                const opts = getLookupOpts(config, fCfg.lookupSource);
                const found = opts.find(o => o.name === value);
                // When a site is chosen, auto-fill all site attribute columns
                // (legacy SampleRegistrationViewController StationLocation callback).
                if (col.field === 'SiteName' && found) {
                  mutate(draft => {
                    const s = draft.find(x => x.id === row.id);
                    if (!s) return;
                    s.v['SiteName']           = found.oid;
                    s.v['Address']            = found.address;
                    s.v['SamplingAddress']    = found.samplingAddress;
                    s.v['KeyMap']             = found.keyMap;
                    s.v['SamplePointID']      = found.samplePointId;
                    s.v['SamplePointType']    = found.samplePointType;
                    s.v['PWSID']              = found.pwsId;
                    s.v['WaterType']          = found.waterTypeName;
                    // Auto-fill FK OIDs from site attributes
                    if (found.systemTypeOid)        s.v['SystemType']        = found.systemTypeOid;
                    if (found.rejectionCriteriaOid)  s.v['RejectionCriteria'] = found.rejectionCriteriaOid;
                    if (found.parentSampleID)        s.v['ParentSampleID']    = found.parentSampleID;
                    if (found.parentSampleDate)      s.v['ParentSampleDate']  = found.parentSampleDate;
                  });
                  return;
                }
                setCell(row.id, col.field, found?.oid ?? value);
                return;
              }
            }
            setCell(row.id, col.field, value);
          }}
          defaultPageSize={100}
          emptyText="No samples yet"
        />
      </div>

      {ctx && (
        <CellContextMenu
          value={ctx.value}
          columnLabel={ctx.label}
          selectedCount={sel.size}
          position={{ x: ctx.x, y: ctx.y }}
          onCopyAll={() => copyColumn(ctx.col, ctx.value, false)}
          onCopySelected={() => {
            if (sel.size === 0) { onWarn('Tick at least one row first.'); return; }
            copyColumn(ctx.col, ctx.value, true);
          }}
          onClose={() => setCtx(null)}
        />
      )}

      </div>{/* end sr-sl-samples */}

      {/* Test Assignment for the current record */}
      <div className="sr-sl-panel sr-sl-tests">
      <TestAssignment
        jobId={jobId} samples={samples} setSamples={setSamples} active={active}
        onWarn={onWarn} onSuccess={onSuccess} testRows={testRows} groupChildren={groupChildren}
      />
      </div>{/* end sr-sl-tests */}

      {/* Copy Samples */}
      {copyOpen && (
        <AppDialog open title="Copy Samples" icon="ti-copy" width={520} onClose={() => setCopyOpen(false)}
          footer={<>
            <button className="ca-btn" onClick={() => setCopyOpen(false)}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={runCopySamples}>OK</button>
          </>}>
          <p style={{ fontSize: 12.5, marginTop: 0 }}>
            Clones the marked sample <b>{samples[active]?.id}</b> — every field, its tests and its
            bottles — the number of times below. New rows are added after it; edit names, locations
            and collection date &amp; time per sample afterwards.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>No Of Samples</label>
            <span className="sr-numspin">
              <button type="button" onClick={() => setCopyCount(c => Math.max(1, c - 1))}>−</button>
              <input value={copyCount} readOnly aria-label="Number of copies" />
              <button type="button" onClick={() => setCopyCount(c => c + 1)}>＋</button>
            </span>
          </div>
        </AppDialog>
      )}

      {/* Copy Tests to Samples */}
      {copyTestOpen && (
        <CopyTestDialog
          samples={samples} active={active}
          onClose={() => setCopyTestOpen(false)}
          onApply={(fromId, targetIds) => {
            mutate(draft => {
              const src = draft.find(s => s.id === fromId);
              if (!src) return;
              for (const s of draft) {
                if (s.id === fromId || !targetIds.includes(s.id)) continue;
                s.tests = src.tests.map(cloneTest);
                autoBottles(s);
              }
            });
            setCopyTestOpen(false);
            onSuccess(`Tests copied from ${fromId} to ${targetIds.length} sample(s).`);
          }}
          onWarn={onWarn}
        />
      )}

      {/* Add Site */}
      {siteOpen && <AddSiteDialog onClose={() => setSiteOpen(false)} onAdd={name => {
        setSiteOpen(false);
        onSuccess(`Site "${name}" added.`);
      }} onWarn={onWarn} />}
    </div>
  );
}

function CopyTestDialog({ samples, active, onClose, onApply, onWarn }: {
  samples: SrSample[]; active: number; onClose: () => void;
  onApply: (fromId: string, targetIds: string[]) => void; onWarn: (m: string) => void;
}) {
  const [from, setFrom] = useState(samples[active]?.id ?? samples[0]?.id ?? '');
  const [targets, setTargets] = useState<string[]>([]);
  const toggle = (id: string) =>
    setTargets(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id]));

  return (
    <AppDialog open title="Copy Tests to Samples" icon="ti-copy" width={520} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => {
          if (targets.length === 0) { onWarn('Tick at least one target sample.'); return; }
          onApply(from, targets);
        }}>OK</button>
      </>}>
      <div className="sr-fgrid" style={{ gridTemplateColumns: '110px minmax(0,1fr)', marginBottom: 10 }}>
        <label>Copy from</label>
        <select value={from} onChange={e => { setFrom(e.target.value); setTargets(t => t.filter(x => x !== e.target.value)); }}>
          {samples.map(s => <option key={s.id}>{s.id}</option>)}
        </select>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5 }}>Copy to:</div>
      <div style={{ border: '1px solid var(--ai-line)', borderRadius: 5, padding: '8px 12px', maxHeight: 180, overflow: 'auto' }}>
        {/* The source can't be copied onto itself — it is hidden from the list. */}
        {samples.filter(s => s.id !== from).map(s => (
          <label key={s.id} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 12.5, padding: '2px 0' }}>
            <input type="checkbox" checked={targets.includes(s.id)} onChange={() => toggle(s.id)} />
            {s.id}
          </label>
        ))}
      </div>
    </AppDialog>
  );
}

function AddSiteDialog({ onClose, onAdd, onWarn }: {
  onClose: () => void; onAdd: (name: string) => void; onWarn: (m: string) => void;
}) {
  const [name, setName] = useState('');
  const [addr, setAddr] = useState('');
  return (
    <AppDialog open title="Site" icon="ti-map-pin" width={480} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => {
          if (!name.trim()) { onWarn('Enter the Site Name.'); return; }
          onAdd(name.trim());
        }}>OK</button>
      </>}>
      <div className="sr-fgrid" style={{ gridTemplateColumns: '110px minmax(0,1fr)' }}>
        <label className="req">Site Name</label>
        <input value={name} className={name ? undefined : 'sr-req-empty'} onChange={e => setName(e.target.value)} />
        <label>Address</label>
        <input value={addr} onChange={e => setAddr(e.target.value)} />
      </div>
    </AppDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Test Assignment — the current sample's tests (BTLIMS grid design)
// ─────────────────────────────────────────────────────────────────────────────
function TestAssignment({ jobId, samples, setSamples, active, onWarn, onSuccess, testRows = [], groupChildren = {} }: {
  jobId: string; samples: SrSample[]; setSamples: (n: SrSample[]) => void; active: number;
  onWarn: (m: string) => void; onSuccess: (m: string) => void; testRows?: string[][];
  groupChildren?: Record<string, { name: string; method: string; comp: string }[]>;
}) {
  const sample = samples[active];
  const [focus, setFocus] = useState(0);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [paramFor, setParamFor] = useState<string | null>(null);   // test key
  const [bottleFor, setBottleFor] = useState<string | null>(null); // test key
  const bl = useBottleLookups();

  const mutate = useCallback((fn: (s: SrSample, draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    const target = draft[active];
    if (!target) return;
    fn(target, draft);
    normBottle(target);
    setSamples(draft);
  }, [samples, active, setSamples]);

  const [, setPV] = useState(0);
  const testKeys = sample?.tests.map(t => paramCacheKey(t)).join(',') ?? '';
  useEffect(() => {
    if (!sample) return;
    let cancel = false;
    const uncached = sample.tests.filter(t => !cachedParams(t));
    if (uncached.length > 0) {
      Promise.all(uncached.map(fetchTestParams)).then(() => { if (!cancel) setPV(v => v + 1); });
    }
    return () => { cancel = true; };
  }, [testKeys]);

  useEffect(() => {
    if (sample && focus >= sample.tests.length) setFocus(Math.max(0, sample.tests.length - 1));
  }, [sample, focus]);

  if (!sample) return null;

  const cols: AppColumn<SrTest>[] = [
    { field: 'name', header: 'Test', width: 170, render: t => <span style={{ fontWeight: 600 }}>{t.name}</span> },
    { field: 'testGroupName', header: 'TestGroupName', width: 150, sortable: true, filterable: true,
      render: t => t.testGroupName || '' },
    {
      field: 'bottle', header: 'BottleID', width: 90, sortable: false,
      render: t => (
        <button className="sr-edit-cell" style={{ color: 'var(--ai-pill-info-fg)', fontWeight: 600 }}
          onClick={e => { e.stopPropagation(); setBottleFor(t.key); }}>
          {t.bottle || '—'} ▾
        </button>
      ),
      text: t => t.bottle,
    },
    { field: 'method', header: 'Method', width: 120 },
    { field: 'comp', header: 'Component', width: 110 },
    {
      field: 'params', header: 'Parameter', width: 120, sortable: false,
      render: t => (
        <button className="sr-edit-cell" style={{ color: 'var(--ai-pill-info-fg)', fontWeight: 600 }}
          onClick={e => { e.stopPropagation(); setParamFor(t.key); }}>
          {paramLabel(t)} ▾
        </button>
      ),
      text: t => paramLabel(t),
    },
    { field: 'storage', header: 'Storage', width: 100 },
    { field: 'hdTime', header: 'HoldTime', width: 110 },
    {
      field: 'status', header: 'Status', width: 140,
      edit: { type: 'combo', options: SAMPLE_STATUS_OPTS },
    },
    {
      field: 'remark', header: 'Remark', width: 70, align: 'center', sortable: false, filterable: false,
      render: t => (
        <input type="checkbox" checked={t.remark} aria-label={`Remark for ${t.name}`}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            const checked = e.target.checked;
            mutate(s => { const x = s.tests.find(y => y.key === t.key); if (x) x.remark = checked; });
          }} />
      ),
    },
    {
      field: 'subOut', header: 'SubOut', width: 70, align: 'center', sortable: false, filterable: false,
      render: t => (
        <input type="checkbox" checked={t.subOut} disabled
          aria-label={`Sub out ${t.name}`}
          title="Driven by Status — set status to 'Sub Out' to activate"
          onClick={e => e.stopPropagation()} />
      ),
    },
    { field: 'added', header: 'TestAddedDate', width: 130, type: 'date' },
  ];

  const paramTest = paramFor ? sample.tests.find(t => t.key === paramFor) : null;
  const bottleTest = bottleFor ? sample.tests.find(t => t.key === bottleFor) : null;

  return (
    <section className="sr-sec">
      <h3 className="sr-sec-h">
        Test Assignment - <span style={{ color: 'var(--brand-accent-hover, var(--ai-orange-hover))' }}>{sample.id}</span>
        <span className="sr-sec-note">
          mark a Sample Login row above to switch sample · ＋ adds a test · Parameter opens the analyte checklist
        </span>
      </h3>
      <div className="sr-sec-b">
        <div className="sr-subtoolbar" style={{ paddingTop: 0 }}>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="ca-btn" onClick={() => setCatalogOpen(true)}>
              <i className="ti ti-plus" /> Add
            </button>
            <button className="ca-btn" disabled={sel.size === 0}
              onClick={() => {
                mutate(s => {
                  s.tests = s.tests.filter(t => !sel.has(t.key));
                  autoBottles(s);
                });
                setSel(new Set());
                onSuccess(`${sel.size} test(s) removed.`);
              }}>
              <i className="ti ti-trash" style={{ color: 'var(--ai-icon-danger, #c0392b)' }} /> Delete
            </button>
          </span>
        </div>
        <AppDataGrid<SrTest>
          rows={sample.tests}
          columns={cols}
          getRowId={t => t.key}
          selectable selectedIds={sel} onSelectionChange={setSel}
          onRowClick={t => setFocus(sample.tests.findIndex(x => x.key === t.key))}
          onCellCommit={(row, col, value) => {
            mutate(s => {
              const x = s.tests.find(y => y.key === row.key);
              if (x && col.field === 'status') {
                x.status = value;
                x.subOut = value === 'Sub Out';
              }
            });
          }}
          defaultPageSize={20}
          emptyText="No tests assigned yet — click Add."
        />
      </div>

      {catalogOpen && (
        <TestCatalogDialog
          assigned={sample.tests}
          testRows={testRows}
          onClose={() => setCatalogOpen(false)}
          onAdd={picks => {
            const added: { name: string; method: string; comp: string; grpName: string }[] = [];
            mutate(s => {
              for (const p of picks) {
                const isGroup = p[2] === '1';
                if (isGroup) {
                  const children = groupChildren[p[0]] ?? [];
                  for (const ch of children) {
                    if (s.tests.some(t => t.name === ch.name && t.comp === (ch.comp || 'Default'))) continue;
                    s.tests.push(mkTest(ch.name, ch.method, ch.comp || undefined, sample.v.EnteredDate ?? '', p[0]));
                    added.push({ name: ch.name, method: ch.method, comp: ch.comp || 'Default', grpName: p[0] });
                  }
                } else {
                  const comp = p[3] || 'Default';
                  if (s.tests.some(t => t.name === p[0] && t.comp === comp)) continue;
                  s.tests.push(mkTest(p[0], p[1], comp, sample.v.EnteredDate ?? ''));
                  added.push({ name: p[0], method: p[1], comp, grpName: '' });
                }
              }
              autoBottles(s);
            });
            for (const a of added) {
              fetchTestParams({ name: a.name, method: a.method, comp: a.comp })
                .then(() => setPV(v => v + 1));
            }
            setCatalogOpen(false);
            onSuccess(`${added.length} test(s) added to ${sample.id}.`);
          }}
          onWarn={onWarn}
        />
      )}

      {paramTest && (
        <ParameterPickerDialog
          test={paramTest}
          onClose={() => setParamFor(null)}
          onApply={(params, paramData) => {
            mutate(s => {
              const x = s.tests.find(y => y.key === paramTest.key);
              if (x) { x.params = params; x.paramData = paramData; }
            });
            setParamFor(null);
          }}
        />
      )}

      {bottleTest && (
        <BottlePickerDialog
          sample={sample}
          test={bottleTest}
          jobId={jobId}
          bl={bl}
          onClose={() => setBottleFor(null)}
          onApply={(bottles, bd) => {
            mutate(s => {
              const x = s.tests.find(y => y.key === bottleTest.key);
              if (!x) return;
              x.bottle = bottles.join(',');
              x.bd = bd;
              const first = bottles[0];
              if (first && bd[first]) {
                x.container = bd[first]!.container;
                x.preserv = bd[first]!.preserv;
                x.storage = bd[first]!.storage;
              }
              syncQty(s);
            });
            setBottleFor(null);
            onSuccess(`Bottle(s) ${bottles.join(',') || '—'} assigned.`);
          }}
        />
      )}
    </section>
  );
}

/** New-row test picker — the CheckedComboBox catalog grid (filter + Add). */
function TestCatalogDialog({ assigned, testRows, onClose, onAdd, onWarn }: {
  assigned: SrTest[]; testRows: string[][]; onClose: () => void;
  onAdd: (picks: [string, string, string, string][]) => void; onWarn: (m: string) => void;
}) {
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  // testRows format from API: [name, method, isGroup, component]
  const assignedKeys = useMemo(() => new Set(assigned.map(t => `${t.name}|${t.comp}`)), [assigned]);
  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return testRows.map((r, i) => ({ r, i }))
      .filter(({ r }) => !assignedKeys.has(`${r[0] ?? ''}|${r[3] || 'Default'}`))
      .filter(({ r }) => !q || r.join(' ').toLowerCase().includes(q));
  }, [filter, testRows, assignedKeys]);

  return (
    <AppDialog open title="Tests" icon="ti-flask" width={660} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => {
          if (picked.size === 0) { onWarn('Tick at least one test.'); return; }
          onAdd([...picked].map(i => {
            const r = testRows[i]!;
            return [r[0], r[1], r[2], r[3]] as [string, string, string, string];
          }));
        }}>Add</button>
      </>}>
      <div className="sr-pick-f" style={{ border: '1px solid var(--ai-line)', borderRadius: 5, marginBottom: 8 }}>
        <input placeholder="Filter test…" value={filter} onChange={e => setFilter(e.target.value)} />
      </div>
      <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
        <table className="sr-pick-table">
          <thead>
            <tr>
              <th className="sr-pick-cb">All</th>
              <th>Test</th>
              <th style={{ width: 70, textAlign: 'center' }}>IsGroup</th>
              <th>Method</th>
              <th>Component</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td className="sr-pick-empty" colSpan={5}>No matches.</td></tr>
            ) : rows.map(({ r, i }) => (
                <tr key={`${r[0]}-${i}`} className={picked.has(i) ? 'on' : ''}
                  onClick={() => {
                    setPicked(p => { const n = new Set(p); if (n.has(i)) n.delete(i); else n.add(i); return n; });
                  }}>
                  <td className="sr-pick-cb">
                    <input type="checkbox" checked={picked.has(i)}
                      aria-label={r[0]} onChange={() => {}} />
                  </td>
                  <td>{r[0]}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={r[2] === '1'} readOnly
                      style={{ pointerEvents: 'none' }} aria-label="Group test" />
                  </td>
                  <td>{r[1]}</td>
                  <td>{r[3]}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="sr-pick-g" style={{ border: 'none', background: 'none', paddingLeft: 0 }}>
        <span>{picked.size} selected · {rows.length} items</span>
        <span>{filter ? `Contains([Test], '${filter}')` : ''}</span>
      </div>
    </AppDialog>
  );
}

/** ParamList checklist — "n of total" + the field-method result columns.
 *  Parameters are loaded from dbo.Testparameter via the API (QCType=Sample,
 *  filtered by TestMethod + Component, excluding surrogates/internal standards). */
function ParameterPickerDialog({ test, onClose, onApply }: {
  test: SrTest; onClose: () => void;
  onApply: (params: Record<string, boolean>, paramData: Record<string, SrParamData>) => void;
}) {
  const field = isFieldTest(test);
  const [list, setList] = useState<string[]>(() => cachedParams(test) ?? []);
  const [loading, setLoading] = useState(!cachedParams(test));
  const [params, setParams] = useState<Record<string, boolean>>(() => test.params ?? {});
  const [data, setData] = useState<Record<string, SrParamData>>(() => ({ ...test.paramData }));

  useEffect(() => {
    let cancel = false;
    fetchTestParams(test).then(names => {
      if (cancel) return;
      setList(names);
      setLoading(false);
      setParams(prev => {
        if (Object.keys(prev).length > 0) return prev;
        return Object.fromEntries(names.map(p => [p, true]));
      });
    });
    return () => { cancel = true; };
  }, [test.name, test.method, test.comp]);

  const on = list.filter(p => params[p] !== false).length;

  const setField = (pm: string, k: keyof SrParamData, v: string) =>
    setData(d => ({
      ...d,
      [pm]: { result: '', units: '', analysisDate: '', analyzedBy: '', ...(d[pm] ?? {}), [k]: v },
    }));

  return (
    <AppDialog open title={`${test.name} — parameters`} icon="ti-list-check"
      width={field ? 800 : 420} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={loading || on < 1}
          onClick={() => onApply(params, data)}>Apply</button>
      </>}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--ai-muted)' }}>
          Loading parameters…
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>{on} of {list.length}</span>
            <span className="sr-hint-inline">
              {field
                ? 'Field method — record on-site results below'
                : 'Lab method — results are entered in Result Entry'}
            </span>
          </div>
          <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
            <table className="sr-pick-table">
              <thead>
                <tr>
                  <th className="sr-pick-cb">
                    <input type="checkbox" checked={on === list.length}
                      aria-label="Select all parameters"
                      onChange={e => {
                        if (!e.target.checked && on <= 1) return;
                        setParams(Object.fromEntries(list.map(p => [p, e.target.checked || p === list[0]])));
                      }} />
                  </th>
                  <th>Parameter</th>
                  {field && <>
                    <th style={{ width: 96 }}>Result</th>
                    <th style={{ width: 66 }}>Units</th>
                    <th style={{ width: 180 }}>AnalysisDate</th>
                    <th style={{ width: 120 }}>AnalyzedBy</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {list.map(pm => {
                  const d = data[pm];
                  return (
                    <tr key={pm}>
                      <td className="sr-pick-cb">
                        <input type="checkbox" checked={params[pm] !== false} aria-label={pm}
                          onChange={e => {
                            if (!e.target.checked && on <= 1) return;
                            setParams(p => ({ ...p, [pm]: e.target.checked }));
                          }} />
                      </td>
                      <td>{pm}</td>
                      {field && <>
                        <td><input value={d?.result ?? ''} aria-label={`${pm} result`}
                          onChange={e => setField(pm, 'result', e.target.value)} style={CELL_INPUT} /></td>
                        <td><input value={d?.units ?? ''} aria-label={`${pm} units`}
                          onChange={e => setField(pm, 'units', e.target.value)} style={CELL_INPUT} /></td>
                        <td><input type="datetime-local" step={60} value={d?.analysisDate ?? ''}
                          aria-label={`${pm} analysis date`}
                          className={!(d?.analysisDate ?? '') ? 'sr-dt-empty' : undefined}
                          onChange={e => setField(pm, 'analysisDate', e.target.value)} style={CELL_INPUT} /></td>
                        <td><input value={d?.analyzedBy ?? ''} aria-label={`${pm} analyzed by`}
                          onChange={e => setField(pm, 'analyzedBy', e.target.value)} style={CELL_INPUT} /></td>
                      </>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AppDialog>
  );
}

const CELL_INPUT: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', border: '1px solid var(--ai-line)',
  borderRadius: 3, padding: '3px 6px', fontSize: 12,
};

/** BottleID editor — the sample's Qty drives the bottle list ({SampleID}-A…). */
function BottlePickerDialog({ sample, test, jobId, onClose, onApply, bl }: {
  sample: SrSample; test: SrTest; jobId: string; onClose: () => void;
  onApply: (bottles: string[], bd: Record<string, import('./srData').SrBottleDetail>) => void;
  bl: BottleLookups;
}) {
  const qty = Math.max(1, Number.parseInt(sample.v.Qty ?? '1', 10) || 1);
  const letters = Array.from({ length: qty }, (_, i) => String.fromCharCode(65 + i));
  const [picked, setPicked] = useState<Set<string>>(() => {
    const s = new Set((test.bottle || '').split(',').filter(Boolean));
    if (s.size === 0) s.add('A');
    return s;
  });
  const [bd, setBd] = useState<Record<string, import('./srData').SrBottleDetail>>(() => {
    const d = testDefaults(test.name);
    const seed: Record<string, import('./srData').SrBottleDetail> = { ...(test.bd ?? {}) };
    for (const L of letters) {
      seed[L] ??= { container: d.container, preserv: d.preserv, storage: '', ph: '', temp: '', paper: '', thermo: '' };
    }
    return seed;
  });

  const set = (L: string, k: keyof import('./srData').SrBottleDetail, v: string) =>
    setBd(m => ({ ...m, [L]: { ...m[L]!, [k]: v } }));

  return (
    <AppDialog open width={1000} icon="ti-bottle"
      title={`${test.name} · bottles (Qty ${qty})`} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid"
          onClick={() => onApply([...picked].sort(), bd)}>Apply</button>
      </>}>
      <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
        <table className="sr-pick-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: '3%' }} />
              <th style={{ width: '16%' }}>Bottle ID</th>
              <th style={{ width: '17%' }}>Container</th>
              <th style={{ width: '13%' }}>Preservation</th>
              <th style={{ width: '6%' }}>pH</th>
              <th style={{ width: '11%' }}>Temperature</th>
              <th style={{ width: '11%' }}>ph Paper ID</th>
              <th style={{ width: '12%' }}>Thermometer ID</th>
              <th style={{ width: '11%' }}>Storage</th>
            </tr>
          </thead>
          <tbody>
            {letters.map(L => {
              const d = bd[L]!;
              const on = picked.has(L);
              return (
                <tr key={L} className={on ? 'on' : ''}>
                  <td className="sr-pick-cb">
                    <input type="checkbox" checked={on} aria-label={`Bottle ${L}`}
                      onChange={() => setPicked(p => {
                        const n = new Set(p);
                        if (n.has(L)) n.delete(L); else n.add(L);
                        return n;
                      })} />
                  </td>
                  <td style={{ fontWeight: 600 }}>{sample.id || `${jobId}-001`}-{L}</td>
                  <td>
                    <select value={d.container} aria-label={`Bottle ${L} container`}
                      onChange={e => set(L, 'container', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {bl.containers.map(c => <option key={c.oid} value={c.name}>{c.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={d.preserv} aria-label={`Bottle ${L} preservation`}
                      onChange={e => set(L, 'preserv', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {bl.preservatives.map(p => <option key={p.oid} value={p.name}>{p.name}</option>)}
                    </select>
                  </td>
                  <td><input value={d.ph} aria-label={`Bottle ${L} pH`}
                    onChange={e => set(L, 'ph', e.target.value)} style={CELL_INPUT} /></td>
                  <td><input value={d.temp} aria-label={`Bottle ${L} temperature`}
                    onChange={e => set(L, 'temp', e.target.value)} style={CELL_INPUT} /></td>
                  <td><input value={d.paper} aria-label={`Bottle ${L} pH paper id`}
                    onChange={e => set(L, 'paper', e.target.value)} style={CELL_INPUT} /></td>
                  <td><input value={d.thermo} aria-label={`Bottle ${L} thermometer id`}
                    onChange={e => set(L, 'thermo', e.target.value)} style={CELL_INPUT} /></td>
                  <td>
                    <select value={d.storage} aria-label={`Bottle ${L} storage`}
                      onChange={e => set(L, 'storage', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {bl.storages.map(s => <option key={s.oid} value={s.name}>{s.name}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="sr-pick-g" style={{ border: 'none', background: 'none', paddingLeft: 0 }}>
        <span>Container &amp; Preservation are pre-filled from the test's defaults — edit to override.</span>
        <span>{picked.size} of {qty} selected</span>
      </div>
    </AppDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Test View tab — one row per sample × test
// ─────────────────────────────────────────────────────────────────────────────
interface FlatTest { key: string; sample: SrSample; test: SrTest }

function TestViewTab({ samples, setSamples, onSuccess, confirmAction, matrixMap }: SampleLoginViewProps) {
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [paramFor, setParamFor] = useState<{ sampleId: string; testKey: string } | null>(null);

  // Ensure paramLabel shows 'All Param' / 'Customized' (not '…') by fetching
  // any uncached test-parameter lists and triggering a re-render on completion.
  const [pVer, setPVer] = useState(_paramVer);
  useEffect(() => {
    let cancel = false;
    const unique = new Map<string, { name: string; method: string; comp: string }>();
    for (const s of samples) for (const t of s.tests) {
      const k = paramCacheKey(t);
      if (!_paramCache.has(k)) unique.set(k, t);
    }
    if (unique.size === 0) return;
    Promise.all([...unique.values()].map(fetchTestParams)).then(() => {
      if (!cancel) setPVer(_paramVer);
    });
    return () => { cancel = true; };
  }, [samples]);

  const rows: FlatTest[] = useMemo(
    () => samples.flatMap(s => s.tests.map(t => ({ key: `${s.id}|${t.key}`, sample: s, test: t }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [samples, pVer],
  );

  const mutate = (fn: (draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    fn(draft);
    setSamples(draft);
  };

  const paramTest = paramFor
    ? samples.find(s => s.id === paramFor.sampleId)?.tests.find(t => t.key === paramFor.testKey) ?? null
    : null;

  const cols: AppColumn<FlatTest>[] = [
    { field: 'sampleId', header: 'SampleID', width: 150, text: r => r.sample.id,
      render: r => <span style={{ fontWeight: 600 }}>{r.sample.id}</span> },
    { field: 'matrix', header: 'Matrix', width: 120,
      text: r => matrixMap?.[(r.sample.v.SampleMatrix ?? '').toLowerCase()] ?? r.sample.v.SampleMatrix ?? '' },
    { field: 'test', header: 'Test', width: 220, text: r => r.test.name },
    { field: 'testGroupName', header: 'TestGroupName', width: 150, sortable: true, filterable: true,
      text: r => r.test.testGroupName, render: r => r.test.testGroupName || '' },
    { field: 'method', header: 'Method', width: 130, text: r => r.test.method },
    { field: 'comp', header: 'Component', width: 110, text: r => r.test.comp },
    {
      field: 'status', header: 'Status', width: 150,
      text:  r => r.test.status,
      value: r => r.test.status,
      edit: { type: 'combo', options: SAMPLE_STATUS_OPTS },
    },
    {
      field: 'param', header: 'Parameter', width: 130, sortable: false,
      render: r => (
        <button className="sr-edit-cell" style={{ color: 'var(--ai-pill-info-fg)', fontWeight: 600 }}
          onClick={e => { e.stopPropagation(); setParamFor({ sampleId: r.sample.id, testKey: r.test.key }); }}>
          {paramLabel(r.test)} ▾
        </button>
      ),
      text: r => paramLabel(r.test),
    },
  ];

  return (
    <>
      <div className="sr-subtoolbar">
        <span className="sr-sublbl">Test View — all tests per sample</span>
        <button className="ca-btn" disabled={sel.size === 0}
          onClick={() => confirmAction('Delete tests', `Delete ${sel.size} test assignment(s)?`, () => {
            mutate(draft => {
              for (const key of sel) {
                const [sampleId, testKey] = String(key).split('|');
                const s = draft.find(x => x.id === sampleId);
                if (s) s.tests = s.tests.filter(t => t.key !== testKey);
              }
              draft.forEach(autoBottles);
            });
            setSel(new Set());
            onSuccess('Test(s) deleted.');
          }, true)}>
          <span className="tb-i tb-i-red">🗑</span> Delete
        </button>
        <ExportMenu filename="test-view" triggerClassName="ca-btn"
          headers={['SampleID', 'Matrix', 'Test', 'TestGroupName', 'Method', 'Component', 'Status', 'Parameter']}
          rows={rows.map(r => [r.sample.id, matrixMap?.[(r.sample.v.SampleMatrix ?? '').toLowerCase()] ?? '', r.test.name,
            r.test.testGroupName, r.test.method, r.test.comp, r.test.status, paramLabel(r.test)])} />
      </div>
      <AppDataGrid<FlatTest>
        rows={rows} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        onCellCommit={(row, col, value) => {
          if (col.field !== 'status') return;
          mutate(draft => {
            const t = draft.find(s => s.id === row.sample.id)?.tests.find(x => x.key === row.test.key);
            if (t) { t.status = value; t.subOut = value === 'Sub Out'; }
          });
        }}
        defaultPageSize={100}
        emptyText="No tests assigned yet."
      />
      {rows.length === 0 && <p className="sr-note">Assign tests in <b>Sample Login ▸ Test Assignment</b>.</p>}

      {paramTest && paramFor && (
        <ParameterPickerDialog
          test={paramTest}
          onClose={() => setParamFor(null)}
          onApply={(params, paramData) => {
            mutate(draft => {
              const s = draft.find(x => x.id === paramFor.sampleId);
              const x = s?.tests.find(y => y.key === paramFor.testKey);
              if (x) { x.params = params; x.paramData = paramData; }
            });
            setParamFor(null);
          }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Containers tab — By Sample (master-detail) / All in One (flat)
// ─────────────────────────────────────────────────────────────────────────────
function ContainersTab({ samples, setSamples, active, setActive, onSuccess, onWarn }: SampleLoginViewProps) {
  const [view, setView] = useState<'bysample' | 'all'>('bysample');
  const bl = useBottleLookups();

  const mutate = (fn: (draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    fn(draft);
    draft.forEach(normBottle);
    setSamples(draft);
  };

  /** Same bottle = one physical container: set once, propagate to its siblings. */
  const syncBottleField = (sampleId: string, testKey: string, field: 'container' | 'preserv' | 'storage', value: string) => {
    mutate(draft => {
      const s = draft.find(x => x.id === sampleId);
      const t = s?.tests.find(x => x.key === testKey);
      if (!s || !t) return;
      const bottle = t.bottle;
      for (const x of s.tests) if (x.bottle === bottle) x[field] = value;
    });
  };

  const copyBottles = () => {
    const src = samples[active];
    if (!src) { onWarn('Select a source sample.'); return; }
    let n = 0;
    mutate(draft => {
      const source = draft[active];
      if (!source) return;
      for (const s of draft) {
        if (s === source) continue;
        for (const t of s.tests) {
          const m = source.tests.find(x => x.name === t.name && x.comp === t.comp);
          if (!m) continue;
          t.bottle = m.bottle; t.container = m.container; t.preserv = m.preserv; t.storage = m.storage;
          t.bd = m.bd ? Object.fromEntries(Object.entries(m.bd).map(([k, v]) => [k, { ...v }])) : null;
        }
        n += 1;
      }
    });
    onSuccess(`Bottle setup copied from ${src.id} to ${n} sample(s).`);
  };

  const resetBottles = () => {
    const cur = samples[active];
    if (!cur) { onWarn('Select a sample.'); return; }
    mutate(draft => {
      const s = draft[active];
      if (!s) return;
      for (const t of s.tests) {
        const d = testDefaults(t.name);
        t.bottle = 'A'; t.container = d.container; t.preserv = d.preserv; t.storage = ''; t.bd = null;
      }
      autoBottles(s);
    });
    onSuccess(`Bottles reset to defaults for ${cur.id}.`);
  };

  const bottleCols = (sampleId: string): AppColumn<SrTest>[] => [
    { field: 'name', header: 'Test', width: 160, render: t => <span style={{ fontWeight: 600 }}>{t.name}</span> },
    { field: 'bottle', header: 'BottleID', width: 90, text: t => t.bottle || 'A' },
    { field: 'method', header: 'Method', width: 120 },
    {
      field: 'container', header: 'Containers', width: 170, sortable: false,
      render: t => (
        <select value={t.container} aria-label={`${t.name} container`} style={CELL_INPUT}
          onChange={e => syncBottleField(sampleId, t.key, 'container', e.target.value)}>
          <option value="">N/A</option>
          {bl.containers.map(c => <option key={c.oid} value={c.name}>{c.name}</option>)}
        </select>
      ),
      text: t => t.container,
    },
    {
      field: 'preserv', header: 'Preservative', width: 140, sortable: false,
      render: t => (
        <select value={t.preserv} aria-label={`${t.name} preservative`} style={CELL_INPUT}
          onChange={e => syncBottleField(sampleId, t.key, 'preserv', e.target.value)}>
          <option value="">N/A</option>
          {bl.preservatives.map(p => <option key={p.oid} value={p.name}>{p.name}</option>)}
        </select>
      ),
      text: t => t.preserv,
    },
    {
      field: 'storage', header: 'StorageID', width: 130, sortable: false,
      render: t => (
        <select value={t.storage} aria-label={`${t.name} storage`} style={CELL_INPUT}
          onChange={e => syncBottleField(sampleId, t.key, 'storage', e.target.value)}>
          <option value="">N/A</option>
          {bl.storages.map(s => <option key={s.oid} value={s.name}>{s.name}</option>)}
        </select>
      ),
      text: t => t.storage,
    },
    {
      field: 'cond', header: 'StorageCondition', width: 170,
      text: t => {
        const sc = bl.storageConditions.find(sc => sc.name === t.storage);
        return sc ? sc.name : storageCondition(t.storage);
      },
    },
  ];

  const current = samples[active];

  return (
    <>
      <div className="sr-viewbar">
        <button className={`sr-viewtab${view === 'bysample' ? ' on' : ''}`} onClick={() => setView('bysample')}>
          By Sample
        </button>
        <button className={`sr-viewtab${view === 'all' ? ' on' : ''}`} onClick={() => setView('all')}>
          All in One
        </button>
        <span style={{ flex: 1 }} />
        <button className="ca-btn" onClick={copyBottles}><span className="tb-i tb-i-blue">🗎</span> Copy Bottles</button>
        <button className="ca-btn" onClick={resetBottles}><span className="tb-i">↩︎</span> Reset Bottles</button>
      </div>

      {view === 'bysample' ? (
        <div className="sr-split">
          <div className="sr-split-col">
            <div className="sr-split-h">Sample Details</div>
            <AppDataGrid<SrSample>
              rows={samples}
              columns={[
                { field: 'id', header: 'SampleID', width: 150, render: s => <span style={{ fontWeight: 600 }}>{s.id}</span> },
                {
                  field: 'qty', header: 'Qty', type: 'number', width: 60,
                  text: s => s.v.Qty ?? '1',
                  render: s => (
                    <input type="number" min={1} max={26} value={s.v.Qty ?? '1'}
                      aria-label={`${s.id} qty`} style={{ ...CELL_INPUT, width: 50, textAlign: 'center' }}
                      onChange={e => {
                        const val = Math.max(1, Math.min(26, parseInt(e.target.value, 10) || 1));
                        mutate(draft => {
                          const d = draft.find(x => x.id === s.id);
                          if (d) d.v.Qty = String(val);
                        });
                      }} />
                  ),
                },
              ]}
              getRowId={s => s.id}
              selectedRowId={current?.id ?? null}
              onRowClick={s => setActive(samples.findIndex(x => x.id === s.id))}
              defaultPageSize={20}
              showFilters={false}
              emptyText="No samples"
            />
          </div>
          <div className="sr-split-col">
            <div className="sr-split-h">
              <span style={{ flex: 1 }}>
                Test Bottles — <span className="sr-split-id">{current?.id ?? '—'}</span>
              </span>
            </div>
            <AppDataGrid<SrTest>
              rows={current?.tests ?? []}
              columns={bottleCols(current?.id ?? '')}
              getRowId={t => t.key}
              defaultPageSize={20}
              emptyText="No tests assigned to this sample."
            />
          </div>
        </div>
      ) : (
        <AllBottlesGrid samples={samples} syncBottleField={syncBottleField} bl={bl} />
      )}
    </>
  );
}

function AllBottlesGrid({ samples, syncBottleField, bl }: {
  samples: SrSample[];
  syncBottleField: (sampleId: string, testKey: string, field: 'container' | 'preserv' | 'storage', value: string) => void;
  bl: BottleLookups;
}) {
  const rows: FlatTest[] = useMemo(
    () => samples.flatMap(s => s.tests.map(t => ({ key: `${s.id}|${t.key}`, sample: s, test: t }))),
    [samples],
  );
  const cols: AppColumn<FlatTest>[] = [
    { field: 'sampleId', header: 'SampleID', width: 150, text: r => r.sample.id,
      render: r => <span style={{ fontWeight: 600 }}>{r.sample.id}</span> },
    { field: 'test', header: 'Test', width: 170, text: r => r.test.name },
    { field: 'bottle', header: 'BottleID', width: 90, text: r => r.test.bottle || 'A' },
    { field: 'method', header: 'Method', width: 120, text: r => r.test.method },
    {
      field: 'container', header: 'Containers', width: 170, sortable: false, text: r => r.test.container,
      render: r => (
        <select value={r.test.container} aria-label={`${r.sample.id} ${r.test.name} container`} style={CELL_INPUT}
          onChange={e => syncBottleField(r.sample.id, r.test.key, 'container', e.target.value)}>
          <option value="">N/A</option>
          {bl.containers.map(c => <option key={c.oid} value={c.name}>{c.name}</option>)}
        </select>
      ),
    },
    {
      field: 'preserv', header: 'Preservative', width: 140, sortable: false, text: r => r.test.preserv,
      render: r => (
        <select value={r.test.preserv} aria-label={`${r.sample.id} ${r.test.name} preservative`} style={CELL_INPUT}
          onChange={e => syncBottleField(r.sample.id, r.test.key, 'preserv', e.target.value)}>
          <option value="">N/A</option>
          {bl.preservatives.map(p => <option key={p.oid} value={p.name}>{p.name}</option>)}
        </select>
      ),
    },
    {
      field: 'storage', header: 'StorageID', width: 130, sortable: false, text: r => r.test.storage,
      render: r => (
        <select value={r.test.storage} aria-label={`${r.sample.id} ${r.test.name} storage`} style={CELL_INPUT}
          onChange={e => syncBottleField(r.sample.id, r.test.key, 'storage', e.target.value)}>
          <option value="">N/A</option>
          {bl.storages.map(s => <option key={s.oid} value={s.name}>{s.name}</option>)}
        </select>
      ),
    },
    {
      field: 'cond', header: 'StorageCondition', width: 170,
      text: r => {
        const sc = bl.storageConditions.find(sc => sc.name === r.test.storage);
        return sc ? sc.name : storageCondition(r.test.storage);
      },
    },
  ];
  return (
    <>
      <div className="sr-split-h" style={{ borderRadius: '4px 4px 0 0' }}>
        All Test Bottles — {rows.length} row(s) across {samples.length} sample(s)
      </div>
      <AppDataGrid<FlatTest>
        rows={rows} columns={cols} getRowId={r => r.key}
        defaultPageSize={100} emptyText="No tests assigned yet."
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Parameters tab — one row per sample × test × included analyte
// ─────────────────────────────────────────────────────────────────────────────
interface FlatParam { key: string; sample: SrSample; test: SrTest; pm: string }

function ParametersTab({ samples, setSamples, onSuccess, confirmAction, matrixMap }: SampleLoginViewProps) {
  const [sel, setSel] = useState<Set<string | number>>(new Set());

  const [pVer, setPVer] = useState(_paramVer);

  useEffect(() => {
    let cancel = false;
    const unique = new Map<string, { name: string; method: string; comp: string }>();
    for (const s of samples) for (const t of s.tests) {
      const k = paramCacheKey(t);
      if (!_paramCache.has(k)) unique.set(k, t);
    }
    if (unique.size === 0) return;
    Promise.all([...unique.values()].map(fetchTestParams)).then(() => {
      if (!cancel) setPVer(_paramVer);
    });
    return () => { cancel = true; };
  }, [samples]);

  const rows: FlatParam[] = useMemo(
    () => samples.flatMap(s => s.tests.flatMap(t =>
      (cachedParams(t) ?? [t.name])
        .filter(pm => !t.params || t.params[pm] !== false)
        .map(pm => ({ key: `${s.id}|${t.key}|${pm}`, sample: s, test: t, pm })))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [samples, pVer],
  );

  const mutate = (fn: (draft: SrSample[]) => void) => {
    const draft = samples.map(s => ({ ...s, v: { ...s.v }, tests: s.tests.map(copyTest) }));
    fn(draft);
    setSamples(draft);
  };

  const setResult = (r: FlatParam, field: keyof SrParamData, value: string) => {
    mutate(draft => {
      const t = draft.find(s => s.id === r.sample.id)?.tests.find(x => x.key === r.test.key);
      if (!t) return;
      t.paramData[r.pm] = {
        result: '', units: '', analysisDate: '', analyzedBy: '',
        ...(t.paramData[r.pm] ?? {}), [field]: value,
      };
    });
  };

  const resultCell = (r: FlatParam, field: keyof SrParamData, type = 'text'): React.ReactNode => {
    const field_ = isFieldTest(r.test);
    return (
      <input
        type={type}
        step={type === 'datetime-local' ? 60 : undefined}
        value={r.test.paramData[r.pm]?.[field] ?? ''}
        disabled={!field_}
        title={field_ ? undefined : 'Lab method — results are entered in Result Entry'}
        aria-label={`${r.sample.id} ${r.pm} ${field}`}
        style={CELL_INPUT}
        className={type === 'datetime-local' && !(r.test.paramData[r.pm]?.[field] ?? '') ? 'sr-dt-empty' : undefined}
        onChange={e => setResult(r, field, e.target.value)}
      />
    );
  };

  const cols: AppColumn<FlatParam>[] = [
    { field: 'sampleId', header: 'SampleID', width: 150, text: r => r.sample.id,
      render: r => <span style={{ fontWeight: 600 }}>{r.sample.id}</span> },
    { field: 'bottle', header: 'BottleID', width: 90, text: r => r.test.bottle || '—' },
    { field: 'test', header: 'Test', width: 150, text: r => r.test.name },
    { field: 'method', header: 'Method', width: 120, text: r => r.test.method },
    { field: 'matrix', header: 'Matrix', width: 120, text: r => matrixMap?.[r.sample.v.SampleMatrix ?? ''] ?? '' },
    { field: 'pm', header: 'Parameter', width: 170 },
    { field: 'result', header: 'Result', width: 110, sortable: false,
      text: r => r.test.paramData[r.pm]?.result ?? '', render: r => resultCell(r, 'result') },
    { field: 'units', header: 'Units', width: 90, sortable: false,
      text: r => r.test.paramData[r.pm]?.units ?? '', render: r => resultCell(r, 'units') },
    { field: 'analysisDate', header: 'AnalysisDate', width: 190, sortable: false,
      text: r => r.test.paramData[r.pm]?.analysisDate ?? '',
      render: r => resultCell(r, 'analysisDate', 'datetime-local') },
    { field: 'analyzedBy', header: 'AnalyzedBy', width: 140, sortable: false,
      text: r => r.test.paramData[r.pm]?.analyzedBy ?? '', render: r => resultCell(r, 'analyzedBy') },
  ];

  return (
    <>
      <div className="sr-subtoolbar">
        <span className="sr-sublbl">
          Parameters — every analyte for this job (field-method results editable here)
        </span>
        <button className="ca-btn" disabled={sel.size === 0}
          onClick={() => confirmAction('Remove parameters', `Remove ${sel.size} analyte(s) from their tests?`, () => {
            mutate(draft => {
              for (const key of sel) {
                const [sampleId, testKey, pm] = String(key).split('|');
                const t = draft.find(s => s.id === sampleId)?.tests.find(x => x.key === testKey);
                if (!t || !pm) continue;
                t.params ??= Object.fromEntries((cachedParams(t) ?? [t.name]).map(p => [p, true]));
                t.params[pm] = false;
                delete t.paramData[pm];
              }
              for (const s of draft) {
                for (const t of s.tests) {
                  if (!t.params) continue;
                  const on = Object.values(t.params).filter(Boolean).length;
                  if (on < 1) {
                    const first = Object.keys(t.params)[0];
                    if (first) t.params[first] = true;
                  }
                }
              }
            });
            setSel(new Set());
            onSuccess('Parameter(s) removed.');
          }, true)}>
          <span className="tb-i tb-i-red">🗑</span> Delete
        </button>
        <ExportMenu filename="parameters" triggerClassName="ca-btn"
          headers={['SampleID', 'BottleID', 'Test', 'Method', 'Matrix', 'Parameter', 'Result', 'Units', 'AnalysisDate', 'AnalyzedBy']}
          rows={rows.map(r => [r.sample.id, r.test.bottle, r.test.name, r.test.method,
            matrixMap?.[r.sample.v.SampleMatrix ?? ''] ?? '', r.pm,
            r.test.paramData[r.pm]?.result ?? '', r.test.paramData[r.pm]?.units ?? '',
            r.test.paramData[r.pm]?.analysisDate ?? '', r.test.paramData[r.pm]?.analyzedBy ?? ''])} />
      </div>
      <AppDataGrid<FlatParam>
        rows={rows} columns={cols} getRowId={r => r.key}
        selectable selectedIds={sel} onSelectionChange={setSel}
        defaultPageSize={100}
        emptyText="No parameters — assign tests (and pick parameters) first."
      />
    </>
  );
}
